import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map, throwError } from 'rxjs';
import { AuthService } from '../../../app/auth/auth.service';
import { environment } from '../../../environments/environment';
import { CompanyContextService } from '../../company-context.service';
import { DsoTrendItem } from '../dashboard/dashboard.service';

interface DsoByMonthRow {
  ResultSetOrder?: number | null;
  MonthLabel?: string | null;
  AsOfDate?: string | null;
  DsoDays?: number | string | null;
}

interface DsoByMonthData {
  monthlyGraph?: DsoByMonthRow[] | null;
}

interface DsoByMonthApiResponse {
  data?: DsoByMonthData | null;
}

@Injectable({ providedIn: 'root' })
export class DsoByMonthService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);
  private readonly companyContext = inject(CompanyContextService);
  private readonly apiUrl = environment.dsoByMonthApiUrl;

  getDsoByMonth(fromDate: string | null, toDate: string | null): Observable<DsoTrendItem[]> {
    const compId = this.resolveCompanyId();
    if (compId == null) {
      return throwError(() => new Error('A company must be selected before loading DSO by month.'));
    }

    const params = new HttpParams()
      .set('fromDate', fromDate ?? '')
      .set('toDate', toDate ?? '')
      .set('compId', compId);

    return this.http.get<DsoByMonthApiResponse>(this.apiUrl, { params }).pipe(
      map(response => {
        const rows = response.data?.monthlyGraph ?? [];

        return rows
          .filter(row => Number(row.ResultSetOrder) === 1)
          .map(row => {
            const asOfDate = row.AsOfDate ?? '';
            const monthLabel = row.MonthLabel ?? asOfDate;
            const rawDso = row.DsoDays;
            const dso = rawDso == null ? null : Number(rawDso);

            return {
              month: asOfDate.substring(0, 10),
              monthLabel,
              dso: dso != null && Number.isFinite(dso) ? dso : null,
            };
          })
          .filter(row => row.month !== '' || row.monthLabel !== '')
          .sort((left, right) => left.month.localeCompare(right.month));
      })
    );
  }

  private resolveCompanyId(): number | null {
    const user = this.auth.session()?.user;
    if (!user) return null;

    return user.isSuperAdmin
      ? this.companyContext.getSelectedCompanyId(user.id) ?? user.compId
      : user.compId;
  }
}
